/**
 * NOS-7: Wrapper for sections with background image and accessibility overlay.
 * Renders background-image, dark overlay, and content with correct z-index stacking.
 */
import type { ReactNode } from "react"
import { SectionBackgroundProvider } from "./SectionBackgroundContext"

export interface SectionBackgroundWrapperProps {
  backgroundImage: string
  backgroundImageAlt?: string
  overlayOpacity?: number
  children: ReactNode
}

const DEFAULT_OVERLAY_OPACITY = 0.55

export function SectionBackgroundWrapper({
  backgroundImage,
  backgroundImageAlt = "",
  overlayOpacity = DEFAULT_OVERLAY_OPACITY,
  children,
}: SectionBackgroundWrapperProps) {
  const opacity = Math.min(1, Math.max(0, overlayOpacity))
  const ariaLabel = backgroundImageAlt || "Section background"

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{
        position: "relative",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: `rgba(0,0,0,${opacity})`,
          zIndex: 1,
        }}
      />
      <SectionBackgroundProvider value={true}>
        <div style={{ position: "relative", zIndex: 2 }}>{children}</div>
      </SectionBackgroundProvider>
    </div>
  )
}
